import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'

import styles from './PilotsTable.module.scss'

import { BillingProvider, EnterprisePilotItem } from '@/api/generated'
import { getEnterprisePilots, PilotStatusFilter } from '@/api/internal'
import Button from '@/components/Button'
import ConvertToSalesOwnedDialog from '@/pages/app/Enterprises/components/ManageEnterprises/ConvertToSalesOwnedDialog'
import EditPilotEndDialog from '@/pages/app/Pilots/components/EditPilotEndDialog'
import ReactivatePilotDialog from '@/pages/app/Pilots/components/ReactivatePilotDialog'
import { formatPilotDate } from '@/pages/app/Pilots/utils'

const PAGE_SIZE = 20

type TabFilter = 'active' | 'expired' | 'all'

// Statuses we treat as a "dead" pilot — eligible for reactivation. Anything else
// (trialing, active, past_due, etc.) is a live pilot and gets Edit and Convert.
const REACTIVATABLE_STATUSES = new Set(['canceled', 'incomplete_expired'])

const getStatusBadgeClass = (status: string): string => {
  switch (status) {
    case 'trialing':
      return styles.statusActive
    case 'active':
      return styles.statusConverted
    case 'canceled':
      return styles.statusCanceled
    default:
      return styles.statusDefault
  }
}

const columnHelper = createColumnHelper<EnterprisePilotItem>()

const PilotsTable = () => {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'pilot_end', desc: true }])
  const [selectedPilot, setSelectedPilot] = useState<EnterprisePilotItem | null>(null)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isReactivateDialogOpen, setIsReactivateDialogOpen] = useState(false)
  const [convertingPilot, setConvertingPilot] = useState<EnterprisePilotItem | null>(null)
  const [activeTab, setActiveTab] = useState<TabFilter>('active')
  const [page, setPage] = useState(0)

  const handleEditPilot = (pilot: EnterprisePilotItem) => {
    setSelectedPilot(pilot)
    setIsEditDialogOpen(true)
  }

  const handleCloseEditDialog = () => {
    setSelectedPilot(null)
    setIsEditDialogOpen(false)
  }

  const handleReactivatePilot = (pilot: EnterprisePilotItem) => {
    setSelectedPilot(pilot)
    setIsReactivateDialogOpen(true)
  }

  const handleCloseReactivateDialog = () => {
    setSelectedPilot(null)
    setIsReactivateDialogOpen(false)
  }

  const handleTabChange = (tab: TabFilter) => {
    setActiveTab(tab)
    setPage(0) // Reset to first page when filter changes
  }

  // Convert tab to API status filter
  const statusFilter: PilotStatusFilter = activeTab === 'all' ? undefined : activeTab

  const {
    data: pilotsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['enterprise-pilots', statusFilter, page],
    queryFn: async () => {
      const response = await getEnterprisePilots(PAGE_SIZE, page * PAGE_SIZE, statusFilter)
      return response.data
    },
  })

  const pilots = pilotsData?.pilots ?? []
  const total = pilotsData?.total ?? 0
  const totalPages = Math.ceil(total / PAGE_SIZE)

  const columns = [
    columnHelper.accessor('enterprise_name', {
      header: 'Enterprise',
      cell: (info) => <span className={styles.enterpriseName}>{info.getValue()}</span>,
      sortingFn: 'alphanumeric',
    }),
    columnHelper.accessor('cost_center_name', {
      header: 'Team',
      cell: (info) => <span className={styles.enterpriseName}>{info.getValue()}</span>,
      enableSorting: false,
    }),
    columnHelper.accessor('enterprise_id', {
      header: 'Enterprise ID',
      cell: (info) => (
        <span className={styles.enterpriseId} title={info.getValue()}>
          {info.getValue()}
        </span>
      ),
      enableSorting: false,
    }),
    columnHelper.accessor('created_at', {
      header: 'Created',
      cell: (info) => formatPilotDate(info.getValue()),
      sortingFn: (rowA, rowB) => {
        return new Date(rowA.original.created_at).getTime() - new Date(rowB.original.created_at).getTime()
      },
    }),
    columnHelper.accessor('pilot_end', {
      header: 'Pilot End',
      cell: (info) => formatPilotDate(info.getValue()),
      sortingFn: (rowA, rowB) => {
        return new Date(rowA.original.pilot_end).getTime() - new Date(rowB.original.pilot_end).getTime()
      },
    }),
    columnHelper.accessor('status', {
      header: 'Sub status',
      cell: (info) => {
        const status = info.getValue()
        return <span className={`${styles.statusBadge} ${getStatusBadgeClass(status)}`}>{status}</span>
      },
      enableSorting: false,
    }),
    columnHelper.accessor('stripe_subscription_id', {
      header: 'Stripe Sub ID',
      cell: (info) => (
        <span className={styles.stripeId} title={info.getValue() ?? undefined}>
          {info.getValue() ?? '—'}
        </span>
      ),
      enableSorting: false,
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => {
        const pilot = info.row.original
        const isReactivatable = REACTIVATABLE_STATUSES.has(pilot.status)
        return (
          <div className={styles.actionButtons}>
            {isReactivatable ? (
              <Button title="Reactivate" onClick={() => handleReactivatePilot(pilot)} type="light" />
            ) : (
              <>
                <Button title="Edit" onClick={() => handleEditPilot(pilot)} type="light" />
                <Button title="Convert" onClick={() => setConvertingPilot(pilot)} type="dark" />
              </>
            )}
          </div>
        )
      },
    }),
  ]

  const table = useReactTable({
    data: pilots,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  // Calculate display range
  const startItem = total === 0 ? 0 : page * PAGE_SIZE + 1
  const endItem = Math.min((page + 1) * PAGE_SIZE, total)

  return (
    <>
      <div className={styles.tableContainer}>
        <div className={styles.tableHeader}>
          <h3 className={styles.tableTitle}>Pilots</h3>
          <div className={styles.tabFilter}>
            <button
              className={`${styles.tab} ${activeTab === 'active' ? styles.tabActive : ''}`}
              onClick={() => handleTabChange('active')}
            >
              Active
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'expired' ? styles.tabActive : ''}`}
              onClick={() => handleTabChange('expired')}
            >
              Expired
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'all' ? styles.tabActive : ''}`}
              onClick={() => handleTabChange('all')}
            >
              All
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className={styles.loadingState}>Loading pilots...</div>
        ) : error ? (
          <div className={styles.errorState}>Failed to load pilots. Please try again.</div>
        ) : pilots.length === 0 ? (
          <div className={styles.emptyState}>No pilots found.</div>
        ) : (
          <>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                          className={header.column.getCanSort() ? styles.sortable : ''}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getIsSorted() && (
                            <span className={styles.sortIndicator}>
                              {header.column.getIsSorted() === 'desc' ? ' ↓' : ' ↑'}
                            </span>
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={styles.pagination}>
              <span className={styles.paginationInfo}>
                Showing {startItem}-{endItem} of {total}
              </span>
              <div className={styles.paginationButtons}>
                <Button
                  title="Previous"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  type="light"
                  isDisabled={page === 0}
                />
                <Button
                  title="Next"
                  onClick={() => setPage((p) => p + 1)}
                  type="light"
                  isDisabled={page >= totalPages - 1}
                />
              </div>
            </div>
          </>
        )}
      </div>
      <EditPilotEndDialog isOpen={isEditDialogOpen} pilot={selectedPilot} onClose={handleCloseEditDialog} />
      <ReactivatePilotDialog
        isOpen={isReactivateDialogOpen}
        pilot={selectedPilot}
        onClose={handleCloseReactivateDialog}
      />
      {convertingPilot && (
        <ConvertToSalesOwnedDialog
          enterpriseId={convertingPilot.enterprise_id}
          costCenterId={convertingPilot.cost_center_id}
          close={() => setConvertingPilot(null)}
          isSalesOwnedPilot={convertingPilot.billing_provider === BillingProvider.SalesOwned}
        />
      )}
    </>
  )
}

export default PilotsTable
